/** Report cover image base path */
const REPORT_COVER_BASE_PATH = '/pub_assets/images/reports';

/**
 * Report cover image map.
 * Key: semantic identifier returned by the server's `coverImageKey` field.
 * Value: public asset path for the cover image.
 */
export const REPORT_COVER_IMAGE_MAP: Record<string, string> = {
  'market-growth-chart': `${REPORT_COVER_BASE_PATH}/market-growth-chart.png`,
  'data-analysis-abstract': `${REPORT_COVER_BASE_PATH}/data-analysis-abstract.png`,
  'trend-rising-laptop': `${REPORT_COVER_BASE_PATH}/trend-rising-laptop.png`,
  'green-dashboard-glass': `${REPORT_COVER_BASE_PATH}/green-dashboard-glass.png`,
  'bar-chart-3d-blue': `${REPORT_COVER_BASE_PATH}/bar-chart-3d-blue.png`,
  'report-panel-interactive': `${REPORT_COVER_BASE_PATH}/report-panel-interactive.png`,
  'report-document-profile': `${REPORT_COVER_BASE_PATH}/report-document-profile.png`,
  'analytics-platform-3d': `${REPORT_COVER_BASE_PATH}/analytics-platform-3d.png`,
  'green-scroll-report': `${REPORT_COVER_BASE_PATH}/green-scroll-report.png`,
  'data-visualization-purple': `${REPORT_COVER_BASE_PATH}/data-visualization-purple.png`,
  'wave-analysis-platform': `${REPORT_COVER_BASE_PATH}/wave-analysis-platform.png`,
  'report-monitor-isometric': `${REPORT_COVER_BASE_PATH}/report-monitor-isometric.png`,
};

const COVER_IMAGE_KEYS = Object.keys(REPORT_COVER_IMAGE_MAP);

/**
 * Get cover image URL by server-provided key.
 * Falls back to a deterministic image based on the reportId when key is missing or invalid.
 */
export function getReportCoverImage(coverImageKey?: string, reportId?: string): string {
  if (coverImageKey && REPORT_COVER_IMAGE_MAP[coverImageKey]) {
    return REPORT_COVER_IMAGE_MAP[coverImageKey];
  }

  // Deterministic fallback: hash reportId to pick a consistent cover image
  const seed = reportId ?? '';
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % COVER_IMAGE_KEYS.length;
  return REPORT_COVER_IMAGE_MAP[COVER_IMAGE_KEYS[index]];
}

/** Follow status values */
export const FOLLOW_STATUS = {
  NEVER: 0,
  FOLLOWING: 1,
  UNFOLLOWED: 2,
} as const;

/** Report type values */
export const REPORT_TYPE = {
  MARKET_TREND: 1,
  COMPANY_AUDIT: 2,
} as const;

/** Report source values */
export const REPORT_SOURCE = {
  CHAT: 1,
  SCHEDULED: 2,
} as const;

/** Save status values */
export const SAVE_STATUS = {
  UNSAVED: 0,
  SAVED: 1,
} as const;

/**
 * Reports 页面 URL Tab 参数枚举
 * 通过 `?tab=xxx` 进行深链接跳转：
 * - `following`：Following tab（默认）
 * - `saved`：Saved Reports tab
 * - `unfollowed`：Following tab，并打开 Unfollowed 过滤
 */
export enum ReportsTabParam {
  Following = 'following',
  Saved = 'saved',
  Unfollowed = 'unfollowed',
}

/** Reports 页面 URL 查询参数 key */
export const REPORTS_TAB_QUERY_KEY = 'tab';

/**
 * 生成带 tab 参数的 Reports 页面路径
 * @example getReportsTabPath(ReportsTabParam.Saved, '/reports') // => "/reports?tab=saved"
 */
export function getReportsTabPath(tab: ReportsTabParam, basePath: string): string {
  return `${basePath}?${REPORTS_TAB_QUERY_KEY}=${tab}`;
}
